import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createMyBusiness } from "services/businessService";
import Card from "components/card";
import Button from "components/button";
import { useToast } from "components/Toast";
import InputField from "components/fields/InputField";

const BusinessCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    phone: "",
    email: "",
    website: "",
    address: "",
  });

  const categories = [
    "Agriculture",
    "Manufacturing",
    "Trading",
    "Services",
    "Technology",
    "Health",
    "Education",
    "Hospitality",
    "Transportation",
    "Other",
  ];

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.description || !formData.category) {
      toast.showError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await createMyBusiness(formData);
      toast.showSuccess("Business listing created successfully!");
      navigate("/user/businesses");
    } catch (err: any) {
      console.error(err);
      const message =
        err.response?.data?.message || "Failed to create business listing";
      toast.showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
          Create Business Listing
        </h1>
        <Link
          to="/user/businesses"
          className="text-blue-500 hover:underline dark:text-blue-400"
        >
          ← Back to Businesses
        </Link>
      </div>

      <Card extra="w-full p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Business Name */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Business Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your business name"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              required
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your business, products, and services"
              rows={6}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              required
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Minimum 50 characters recommended
            </p>
          </div>

          {/* Phone */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="e.g., +234 800 123 4567"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Email */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="e.g., business@example.com"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Website */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Website (Optional)
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="e.g., https://example.com"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Address */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Business Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter your business location"
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              className="flex-1"
            >
              {loading ? "Creating..." : "Create Business Listing"}
            </Button>
            <Link to="/user/businesses" className="flex-1">
              <Button type="button" variant="outline" className="w-full">
                Cancel
              </Button>
            </Link>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            * Required fields. Your business will be submitted for admin
            approval before it appears publicly.
          </p>
        </form>
      </Card>
    </div>
  );
};

export default BusinessCreate;
